import React, { useState, useEffect } from'react';
import { Search, Info } from'lucide-react';
import { StepHelpers } from'@shared/components/Stepper';
import { getRemoteUtilityId } from'@shared/utils/getUtilityId';
import { useCurrency } from'@shared/selectors/globalSelectors';
import { useUtilityRequestConfiguration } from '../../../../../shared/hook';
import { ServiceSelectionCard } from '../../../../../shared/components/ServiceSelectionCard';
import { SelectedAccountsBanner } from '../../../../../shared/components/SelectedAccountsBanner';
import { useTranslation } from 'react-i18next';


// Category color mapping function
const getCategoryColor = (category: string) => {
 const cat = category?.toLowerCase() ||'';
 if (cat.includes('water') && !cat.includes('waste')) return'bg-blue-600 text-white';
 if (cat.includes('waste') || cat.includes('sewer')) return'bg-emerald-500 text-white';
 if (cat.includes('electric')) return'bg-yellow-500 text-white';
 return'bg-purple-600 text-white';
};

export const SelectComplaintStep = ({
 stepHelpers,
 currentStepIndex = 0,
 onNext,
 onPrevious,
 remoteUtilityId = getRemoteUtilityId(),
 requestType ="Service"
}: {
 stepHelpers?: StepHelpers;
 currentStepIndex?: number;
 onNext?: () => void;
 onPrevious?: () => void;
 remoteUtilityId?: number;
 requestType?: string;
}) => {
 const { t } = useTranslation();
 const [searchTerm, setSearchTerm] = useState('');
 const [selectedId, setSelectedId] = useState<string | null>(null);
 const currency =useCurrency()

 useEffect(() => {
 if (stepHelpers) {
  const data = stepHelpers.getStepData(currentStepIndex);
  if (data) {
  if (data.searchTerm) setSearchTerm(data.searchTerm);
  if (data.selectedId) setSelectedId(data.selectedId);
  }
 }
 }, [stepHelpers, currentStepIndex]);

 const updateSearchTerm = (val: string) => {
 setSearchTerm(val);
 if (stepHelpers) {
  stepHelpers.setStepData(currentStepIndex, { searchTerm: val, selectedId });
 }
 };

 const updateSelectedId = (id: string) => {
 setSelectedId(id);
 if (stepHelpers) {
  stepHelpers.setStepData(currentStepIndex, { searchTerm, selectedId: id });
 }
 };

 const { data: configData, isLoading } = useUtilityRequestConfiguration({
 remote_utility_id: remoteUtilityId,
 requestType: requestType,
 disablePagination: true,
 });

 const filteredServices = (configData?.result || []).filter(s => {
 const searchLower = searchTerm.toLowerCase();
 return (
  (s.name ||'').toLowerCase().includes(searchLower) || 
  (s.longDescription ||'').toLowerCase().includes(searchLower)
 );
 });

 return (
 <div className="space-y-6">
  <SelectedAccountsBanner stepHelpers={stepHelpers} />
  <div>
  <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
   {t("Select Complaint")}
   <Info className="w-4 h-4 text-gray-400" />
  </h3>
  <p className="text-sm text-gray-500">{t("Browse all available complaints and select the one you need")}</p>
  </div>

  <div className="relative">
  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
   <Search className="h-5 w-5 text-gray-400"/>
  </div>
  <input
   type="text"
   className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
   placeholder={t("Search complaints...")}
   value={searchTerm}
   onChange={(e) => updateSearchTerm(e.target.value)}
  />
  </div>

  <div>
  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
   {t("Available Complaints")}
   <Info className="w-4 h-4 text-gray-400" />
  </h4>
  
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
   {isLoading ? (
   <div className="col-span-full py-8 text-center text-gray-500">
    <div className="animate-pulse">{t("Loading available complaints...")}</div>
   </div>
   ) : filteredServices.length === 0 ? (
   <div className="col-span-full py-8 text-center text-gray-500">
    {t("No complaints found matching your search.")}
   </div>
   ) : (
    filteredServices.map((service) => (
      <ServiceSelectionCard 
        key={service.id}
        service={service}
        isSelected={selectedId === service.id?.toString()}
        onSelect={updateSelectedId}
        currency={currency}
      />
    ))
   )}
  </div>
  </div>

  <div className="flex justify-between pt-6 border-t border-gray-200">
  <button
   onClick={onPrevious}
   disabled={currentStepIndex === 0}
   className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-colors ${
   currentStepIndex === 0 
    ?'bg-gray-100 text-gray-400 cursor-not-allowed'
    :'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
   }`}
  >
   {t("Previous")}
  </button>
  <button
   onClick={onNext}
   disabled={!selectedId}
   className={`px-6 py-2.5 rounded-lg text-sm font-medium transition-colors ${
   !selectedId
    ?'bg-blue-300 text-white cursor-not-allowed'
    :'bg-blue-600 text-white hover:bg-blue-700'
   }`}
  >
   {t("Continue")}
  </button>
  </div>
 </div>
 );
};
